import { useQueryClient } from '@tanstack/react-query';

import { RULE_ID, RULE_SUB_CATEGORY_ID } from '~/common/constants';
import { useUser } from '~/common/hooks/useAuthentication';
import useBoltStatus from '~/common/hooks/useBoltStatus';
import { useReadOnlyContext } from '~/common/hooks/useReadOnly';
import type useGetAccountSummaryInformation from '~/pages/Assets/AccountSummary/api/useGetAccountSummaryInformation';
import { keys as accountSummarykeys } from '~/pages/Assets/AccountSummary/api/useGetAccountSummaryInformation';
import type useSingleAsset from '~/pages/Assets/AssetDepositoryAccounts/api/useSingleAsset';
import { keys as singleAssetKeys } from '~/pages/Assets/AssetDepositoryAccounts/api/useSingleAsset';
import { BusinessAssetTypeArray } from '~/pages/Assets/AssetDepositoryAccounts/enum';
import { useNavigation } from '~/stores/navigationStore';

import useUrl from './useUrl';

const useIsWriteAccessDisabled = (): boolean => {
  const { selectedAlert, selectedCategory } = useNavigation();
  const { isUnderwriter, role, hasAccessToUnderwriterView } = useUser();
  const {
    data: { isLoanSubmitted },
  } = useBoltStatus();
  const { isReadOnly } = useReadOnlyContext();
  const queryClient = useQueryClient();
  const { isReadOnly: isUrlReadOnly } = useUrl();

  const isCategoryReadOnlyPermission =
    selectedCategory?.isReadOnlyPermission ?? false;

  if (
    BusinessAssetTypeArray.includes(
      selectedAlert?.additionalReferences?.AssetType,
    ) &&
    isUnderwriter
  ) {
    const isBankStatementAccountReviewOrDspPageRuleIdList = [
      RULE_ID.ASSET_BANK_STATEMENT_RULE_ID,
      RULE_ID.ASSET_ACCOUNT_REVIEW_RULE_ID,
      RULE_ID.ASSET_DEPOSIT_SOURCING_RULE_ID,
    ];

    const isSingleAssetDocumentProvided = queryClient.getQueryData<
      ReturnType<typeof useSingleAsset>['data']
    >(singleAssetKeys.forAsset(selectedAlert.ruleReference?.id))?.screenData
      ?.assets?.isDocumentProvided;

    const isAccountSummaryDocumentProvided = queryClient.getQueryData<
      ReturnType<typeof useGetAccountSummaryInformation>['data']
    >(accountSummarykeys.forAsset(selectedAlert.ruleReference?.id))?.screenData
      ?.asset?.isDocumentProvided;

    const isDocumentProvided =
      isSingleAssetDocumentProvided || isAccountSummaryDocumentProvided;

    const isBankStatementAccountReviewOrDspPage =
      isBankStatementAccountReviewOrDspPageRuleIdList.includes(
        selectedAlert.ruleId,
      );

    return !!(
      selectedAlert?.isReadOnlyPermission &&
      (isDocumentProvided || isBankStatementAccountReviewOrDspPage)
    );
  }

  if (
    selectedAlert?.ruleSubCategoryId === RULE_SUB_CATEGORY_ID.PROPERTY_TAXES
  ) {
    return selectedAlert.isReadOnlyPermission || isReadOnly;
  }

  return !!(
    (!isUnderwriter && !hasAccessToUnderwriterView && isLoanSubmitted) ||
    role.includes('AccountExecutive') ||
    isCategoryReadOnlyPermission ||
    isReadOnly ||
    isUrlReadOnly
  );
};

export default useIsWriteAccessDisabled;
